// Storefront launcher for the "Personalize" app block. No build step: this file is served to the
// browser exactly as written (a theme app extension asset), so it is deliberately dependency-free
// vanilla JS — no TypeScript, no zod, no bundler. Everything that does not need `document`/`window`
// is a pure, exported function, so tests/unit/launcher.test.ts can exercise it under plain Node with
// a fake window/fetch instead of a real DOM. Nothing at module scope touches `document` or `window`;
// see startLauncher() at the very end for the only place that does.
//
// The message protocol this file receives is authored once, as typed zod schemas, in
// web/src/lib/storefront-bridge.ts — the module the popup's own (Vite-bundled) code imports. This
// file cannot import that module: it is a static asset with no build step. Its own copies of the
// approval-reference pattern, the two private property names and the reserved-name pattern are
// therefore asserted BYTE-IDENTICAL to storefront-bridge.ts's by tests/unit/launcher.test.ts (and the
// reserved-name check is run against both files on the same table of names) — change one and that
// test tells you to change the other, instead of the two silently drifting apart. See
// docs/STOREFRONT.md.

export const NC_PROTOCOL_VERSION = 1;

// Must stay byte-identical to APPROVAL_REF_PATTERN_SOURCE in web/src/lib/storefront-bridge.ts.
export const APPROVAL_REF_PATTERN_SOURCE = "^nc(?:d|s)1[0-9a-f]{48}$";
export const APPROVAL_REF_PATTERN = new RegExp(APPROVAL_REF_PATTERN_SOURCE);

const VARIANT_ID_PATTERN = /^[1-9][0-9]{0,19}$/;
const PROPERTY_NAME_MAX = 100;
const PROPERTY_VALUE_MAX = 255;
const PROPERTIES_MAX = 16;
const ADDONS_MAX = 8;
const ADDON_PROPERTIES_MAX = 8;

// The two private linkage properties. Must stay byte-identical to web/src/lib/storefront-bridge.ts's
// constants of the same names (tests/unit/launcher.test.ts). ONE name each, everywhere:
// - DESIGN_REF_PROPERTY goes on the purchased product line. It is the property CustomOS already reads
//   (`DESIGN_REF_PROPERTY` in its design-ref-name.ts, recognised by `isDesignRefProperty`, including
//   ShipStation's leading `_`→space rewrite). `ncd1` names a design package; `ncs1` names a
//   frozen static receipt. Both use this cart grouping helper.
// - DESIGN_ADDON_OF_PROPERTY goes on each add-on line, with the same reference. It must NOT match
//   CustomOS's design-ref name test (raw or after the ShipStation rewrite), or an add-on line would
//   be read as a second design line. docs/handoff/customos-design-package-instructions.md §1b.
export const DESIGN_REF_PROPERTY = "_design_ref";
export const DESIGN_ADDON_OF_PROPERTY = "_design_addon_of";

// A popup-supplied name CustomOS could read as either linkage property: its own design-ref test
// (design-ref-name.ts: PREFIXED_REF_NAME on the raw name, BARE_REF_NAME on the trimmed name — any
// case, any run of space/underscore/hyphen between the words, an optional leading run of `_`/space)
// widened to the add-on name. Tested against the TRIMMED name. Must stay byte-identical to
// storefront-bridge.ts's LINKAGE_LOOKALIKE_PATTERN_SOURCE.
export const LINKAGE_LOOKALIKE_PATTERN_SOURCE = "^[\\s_]*design[\\s_-]*(?:ref|addon[\\s_-]*of)$";
const LINKAGE_LOOKALIKE = new RegExp(LINKAGE_LOOKALIKE_PATTERN_SOURCE, "i");
const PROTOTYPE_NAMES = new Set(["__proto__", "constructor", "prototype"]);

/** True for a name the popup may never send: a leading `_` (Shopify's private namespace) or leading
 * whitespace (what ShipStation turns a leading `_` into, so the same private name in disguise), a
 * name CustomOS could read as a linkage property, or a prototype name. */
export function isReservedPropertyName(name) {
  return /^[\s_]/.test(name) || LINKAGE_LOOKALIKE.test(name.trim()) || PROTOTYPE_NAMES.has(name);
}

/** A Shopify product/variant id: digits only, no leading zero. Accepts a number OR a numeric
 * string (Liquid renders `product.id` unquoted) and always returns a string, or null. */
export function parseNumericId(raw) {
  const s = typeof raw === "number" ? String(raw) : raw;
  return typeof s === "string" && VARIANT_ID_PATTERN.test(s) ? s : null;
}

// ---- The launch (audit 09-27 P2): ONE opening of the popup. Each load of the popup (an open, a reopen,
// the dialog's Retry) gets a fresh random id, on its URL as `&launch=`; the popup echoes it on every
// message it sends (web/src/popup/embed.ts). The launcher hears only the ACTIVE launch's messages: a
// message queued by a popup the buyer already closed, or by the one before a reopen, is dropped before it
// can close, resize or add anything to the cart. Closing the dialog, releasing the inline panel and
// disposing the block end the launch; collapsing the inline panel does not (the buyer's work stays). ----

/** Must stay byte-identical to LAUNCH_ID_PATTERN_SOURCE in web/src/lib/storefront-bridge.ts. */
export const LAUNCH_ID_PATTERN_SOURCE = "^[0-9a-f]{32}$";

/** A fresh launch id: 16 random bytes as 32 lowercase hex. `cryptoImpl` is the page's `crypto`; without
 *  one this throws (the popup does not open), never a guessable id. */
export function newLaunchId(cryptoImpl) {
  const bytes = new Uint8Array(16);
  cryptoImpl.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** The popup's launch URL: `${origin}/popup.html?product=&variant=&parent=` — the query params the
 * popup reads for a storefront launch (web/popup.html, web/src/popup/main.tsx). `parentOrigin` is this
 * storefront page's own `window.location.origin`: the popup posts its messages only there, and it
 * cannot otherwise tell its parent in Firefox (no `location.ancestorOrigins`) or under
 * `Referrer-Policy: no-referrer` (empty `document.referrer`). It checks it against both when present.
 * `fallback` (a FALLBACK_REASONS code, or null): why this popup opens in today's modal instead of the
 * shell asked for, as `&fb=` — popup.html forwards it on its first launch request, and the server counts it.
 * `launch` (newLaunchId, or null): this opening's id, as `&launch=`, echoed on every popup message.
 * `look` (readHostLook, or null): the page's own look, on the FRAGMENT (`#look=`): never sent to the
 * server, never in its logs or caches; the popup validates every value of it (web/src/popup/host-look.ts). */
export function buildPopupUrl(origin, productId, variantId, parentOrigin, fallback = null, launch = null, look = null) {
  const url = new URL("/popup.html", origin);
  url.searchParams.set("product", productId);
  url.searchParams.set("variant", variantId);
  url.searchParams.set("parent", parentOrigin);
  if (fallback) url.searchParams.set("fb", fallback);
  if (launch) url.searchParams.set("launch", launch);
  if (look) url.hash = `look=${encodeURIComponent(JSON.stringify(look))}`;
  return url.href;
}

/** The most @font-face rules readHostLook passes on (the page's body and heading families, each face). */
export const HOST_LOOK_FACES_MAX = 6;

/** `el`'s computed background when it paints one (alpha > 0), else null. */
function paintedBackground(win, el) {
  const bg = win.getComputedStyle(el).backgroundColor;
  if (!bg || bg === "transparent") return null;
  const m = /^rgba\([^)]*,\s*([\d.]+)\s*\)$/.exec(bg) || /\/\s*([\d.]+%?)\s*\)$/.exec(bg);
  if (m && parseFloat(m[1]) === 0) return null;
  return bg;
}

/** The first family of a computed font-family list, unquoted ("'CC Nunito', Arial" → "CC Nunito"). */
export function firstFamilyOf(list) {
  const first = String(list || "").split(",")[0].trim();
  return first.replace(/^["']|["']$/g, "");
}

/** The @font-face rules this page declares for `families` (same-origin and inline sheets only: a
 *  cross-origin sheet's rules cannot be read), each as { family, url, weight, style }: the woff2 source
 *  when there is one, resolved to an absolute URL. At most HOST_LOOK_FACES_MAX. */
export function fontFacesOf(doc, families) {
  const want = new Set(families.filter(Boolean));
  const faces = [];
  if (!want.size) return faces;
  for (const sheet of Array.from(doc.styleSheets || [])) {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // a cross-origin stylesheet (SecurityError)
    }
    for (const rule of Array.from(rules || [])) {
      if (faces.length >= HOST_LOOK_FACES_MAX) return faces;
      if (!rule.style || !/^@font-face/i.test(rule.cssText || "")) continue;
      const family = firstFamilyOf(rule.style.getPropertyValue("font-family"));
      if (!want.has(family)) continue;
      const src = rule.style.getPropertyValue("src") || "";
      const urls = [...src.matchAll(/url\(\s*["']?([^"')]+)["']?\s*\)(\s*format\(\s*["']?([\w-]+)["']?\s*\))?/g)];
      const pick = urls.find((u) => u[3] === "woff2") || urls[0];
      if (!pick) continue;
      let url;
      try {
        url = new URL(pick[1], sheet.href || doc.baseURI).href;
      } catch {
        continue;
      }
      faces.push({ family, url, weight: rule.style.getPropertyValue("font-weight") || "normal", style: rule.style.getPropertyValue("font-style") || "normal" });
    }
  }
  return faces;
}

/**
 * The button's colours and shape as the page shows it at rest. At the click the pointer is ON it: its
 * own computed style is the theme's hover colour (clickedcraft: #365648 over the ink #233b32), or a
 * colour half-way through a transition. A bare copy beside it, never hovered, focused or animated,
 * reads what the theme gives the button itself; it is removed at once (nothing is painted meanwhile).
 */
function restingStyleOf(button, win) {
  const probe = button.cloneNode(false);
  for (const a of ["id", "disabled", "aria-busy", "aria-expanded", "aria-controls"]) probe.removeAttribute(a); // its states, not what styles it
  probe.setAttribute("aria-hidden", "true");
  probe.tabIndex = -1;
  probe.style.cssText += ";position:absolute;visibility:hidden;pointer-events:none;transition:none;animation:none";
  button.parentNode.insertBefore(probe, button);
  try {
    const c = win.getComputedStyle(probe);
    return { backgroundColor: c.backgroundColor, color: c.color, borderTopLeftRadius: c.borderTopLeftRadius, textTransform: c.textTransform, letterSpacing: c.letterSpacing, fontWeight: c.fontWeight };
  } finally {
    probe.remove();
  }
}

/**
 * The page's own look, read at the click (never on page load), for the popup to follow (Emre 09-27:
 * "the app should follow the natural color of the website"): the colour painted behind the block, the
 * block's text colour and font, the page's heading font, the Personalize button's colours and shape,
 * the product form's own label style, and the @font-face rules of those fonts. Plain strings as the
 * browser computed them; the popup accepts only what its own checks pass (host-look.ts). null when
 * the page cannot be read (never an error: the popup keeps its built-in look).
 */
export function readHostLook(root, win) {
  try {
    const doc = root.ownerDocument;
    const cs = (el) => win.getComputedStyle(el);
    let bg = null;
    for (let el = root; el && el.nodeType === 1 && !bg; el = el.parentElement) bg = paintedBackground(win, el);
    const text = cs(root);
    const heading = doc.querySelector("h1");
    const button = root.querySelector("[data-nc-open]");
    const form = root.closest("form");
    // The form's own first label on show (a hidden fallback picker's label is styled for nobody).
    const label = form ? Array.from(form.querySelectorAll("label")).find((l) => !root.contains(l) && !l.closest("[hidden]") && cs(l).display !== "none") : null;
    const b = button ? restingStyleOf(button, win) : null;
    const l = label ? cs(label) : null;
    const font = text.fontFamily;
    const headingFont = heading ? cs(heading).fontFamily : null;
    return {
      bg,
      ink: text.color,
      font,
      heading: headingFont,
      btn: b ? { bg: b.backgroundColor, ink: b.color, radius: b.borderTopLeftRadius, transform: b.textTransform, spacing: b.letterSpacing, weight: b.fontWeight } : null,
      label: l ? { transform: l.textTransform, spacing: l.letterSpacing, size: l.fontSize, weight: l.fontWeight } : null,
      faces: fontFacesOf(doc, [firstFamilyOf(font), headingFont ? firstFamilyOf(headingFont) : null]),
    };
  } catch {
    return null;
  }
}

/** How long the popup has to say nc:ready before the buyer is offered a retry instead of an endless "Loading…". */
export const READY_TIMEOUT_MS = 15_000;

/**
 * A watchdog for nc:ready: once started, `onTimeout` runs once unless `ready()` (or `stop()`, on
 * close) comes first within `ms`. `timers` is `window` (or a fake in tests); `start()` re-arms it.
 */
export function createReadyWatch(timers, ms, onTimeout) {
  let id = null;
  const stop = () => {
    if (id !== null) timers.clearTimeout(id);
    id = null;
  };
  return {
    start() {
      stop();
      id = timers.setTimeout(() => {
        id = null;
        onTimeout();
      }, ms);
    },
    ready: stop,
    stop,
  };
}

function isCartProperty(p) {
  return !!p && typeof p === "object" && typeof p.name === "string" && typeof p.value === "string" &&
    p.name.length >= 1 && p.name.length <= PROPERTY_NAME_MAX &&
    p.value.length >= 1 && p.value.length <= PROPERTY_VALUE_MAX &&
    !isReservedPropertyName(p.name);
}

/** Structural check of a `properties` array: well-formed entries, no reserved/duplicate names. */
export function isCartPropertyList(list, max) {
  if (!Array.isArray(list) || list.length > max) return false;
  const names = new Set();
  for (const p of list) {
    if (!isCartProperty(p)) return false;
    if (names.has(p.name)) return false;
    names.add(p.name);
  }
  return true;
}

/**
 * Structural validation of an incoming `nc:approved` message (the authoritative schema is
 * web/src/lib/storefront-bridge.ts's `NcApprovedPayload`; this is the receiver's own copy of the
 * same checks, per the file-header note above). `expectedVariantId` is the variant this block
 * currently shows — a message for a different variant is refused rather than silently added to the
 * wrong line, in case the buyer changed the option while the popup was open.
 *
 * Returns `{ ok:true, approved }` or `{ ok:false, reason }`. Never throws.
 */
/** `productVariantIds`: every variant of this product (the buyer may switch size, marker or stand
 * INSIDE the popup — Emre's decision 2 — so the approved variant need not be the page's selection). */
export function validateApproved(data, productVariantIds) {
  if (!data || typeof data !== "object") return { ok: false, reason: "not_an_object" };
  if (data.v !== NC_PROTOCOL_VERSION) return { ok: false, reason: "wrong_version" };
  if (data.type !== "nc:approved") return { ok: false, reason: "wrong_type" };
  if (typeof data.approvalRef !== "string" || !APPROVAL_REF_PATTERN.test(data.approvalRef)) return { ok: false, reason: "bad_approval_ref" };
  const variantId = parseNumericId(data.variantId);
  if (!variantId) return { ok: false, reason: "bad_variant_id" };
  if (productVariantIds && !productVariantIds.has(variantId)) return { ok: false, reason: "variant_not_of_product" };
  if (data.quantity !== 1) return { ok: false, reason: "bad_quantity" };
  if (!isCartPropertyList(data.properties, PROPERTIES_MAX)) return { ok: false, reason: "bad_properties" };
  if (!Array.isArray(data.addons) || data.addons.length > ADDONS_MAX) return { ok: false, reason: "bad_addons" };
  const addons = [];
  for (const a of data.addons) {
    const addonVariantId = a && parseNumericId(a.variantId);
    if (!addonVariantId) return { ok: false, reason: "bad_addon_variant_id" };
    const addonProperties = (a && a.properties) ?? [];
    if (!isCartPropertyList(addonProperties, ADDON_PROPERTIES_MAX)) return { ok: false, reason: "bad_addon_properties" };
    addons.push({ variantId: addonVariantId, properties: addonProperties });
  }
  return { ok: true, approved: { approvalRef: data.approvalRef, variantId, properties: data.properties, addons } };
}

/** Shopify's Ajax cart wants a plain object; null-prototype so no property name (allow-listed
 * shape or not) can ever reach Object.prototype. */
function propertiesToObject(reserved, list) {
  const out = Object.assign(Object.create(null), reserved);
  for (const p of list) out[p.name] = p.value;
  return out;
}

/**
 * The ONE `/cart/add.js` body for an approval: the design line first (the approved variant,
 * `_design_ref` plus the human-readable properties the popup sent), then one line per add-on (its
 * variant, `_design_addon_of` holding the same reference, plus its own properties, if any) — a
 * priced option choice, or the fee of a size the buyer chose in the popup (docs/POPUP-OPTIONS.md),
 * which is an add-on line like any other. Every line carries the product page's `quantity` (review
 * finding 4): N copies of the design come with N of each add-on.
 */
export function buildCartAddBody(approved, quantity = 1) {
  const designLine = { id: approved.variantId, quantity, properties: propertiesToObject({ [DESIGN_REF_PROPERTY]: approved.approvalRef }, approved.properties) };
  // Shopify's native nested cart lines keep a static size charge visibly attached
  // to its doormat in checkout. Personalized approvals retain their existing cart
  // shape. CustomOS still verifies the same receipt and fee-line properties.
  const staticParentId = approved.approvalRef?.startsWith("ncs1") ? Number(approved.variantId) : null;
  if (staticParentId !== null && !Number.isSafeInteger(staticParentId)) throw new Error("invalid_static_parent_id");
  const addonLines = approved.addons.map((a) => ({
    id: a.variantId, quantity,
    // The parent's Size property and the child variant already say which size
    // was chosen. A second visible Size property clutters Shopify checkout.
    properties: propertiesToObject({ [DESIGN_ADDON_OF_PROPERTY]: approved.approvalRef }, staticParentId === null ? a.properties : a.properties.filter((p) => p.name !== "Size")),
    ...(staticParentId !== null ? { parent_id: staticParentId } : {}),
  }));
  return { items: [designLine, ...addonLines] };
}

/**
 * The product page's quantity (review finding 4): its quantity field's value as a whole number from
 * 1 to 9999; `raw` null/undefined (the page has no quantity field) is 1. Anything else is null, and
 * the launcher then asks the buyer for a quantity instead of guessing one.
 */
export function parseQuantity(raw) {
  if (raw === undefined || raw === null) return 1;
  const s = String(raw).trim();
  return /^[1-9][0-9]{0,3}$/.test(s) ? Number(s) : null;
}

/**
 * The product form's quantity field: `form.elements` first, which also holds a field placed OUTSIDE
 * the `<form>` and tied to it with `form="…"` (Dawn does this), the first when there are several;
 * then one inside the form; then one naming the form by id. Null when there is none.
 */
export function quantityInputOf(form, doc) {
  if (!form) return null;
  let named = form.elements && typeof form.elements.namedItem === "function" ? form.elements.namedItem("quantity") : null;
  if (named && !named.tagName && typeof named.item === "function") named = named.item(0); // several: a RadioNodeList
  if (named) return named;
  const inside = typeof form.querySelector === "function" ? form.querySelector('[name="quantity"]') : null;
  if (inside) return inside;
  return form.id ? doc.querySelector(`[name="quantity"][form="${String(form.id).replace(/["\\]/g, "\\$&")}"]`) : null;
}

/** At most this many references are remembered per tab by createUncertainRefs (the newest kept). */
export const UNCERTAIN_REFS_MAX = 20;
/** The tab's sessionStorage key createUncertainRefs keeps them under (the storefront's origin). */
export const UNCERTAIN_REFS_KEY = "nc:cart-uncertain-refs";

/**
 * The approval references whose add to the cart is NOT known to have gone in whole: an add still
 * running, a failed one, one whose answer was lost (audit 09-27 P1, and the review of its fix: a retry
 * after a page reload was blind). The next attempt for such a reference reads the cart first.
 * Kept in the page AND, where the browser allows it, in the tab's sessionStorage, so a reload, or View
 * cart then Back to a fresh page load, still reads the cart first. sessionStorage belongs to one tab:
 * another tab, or a new visit, does not share it. Where the browser refuses storage (it throws), only
 * the page's memory is kept (docs/STOREFRONT.md names both limits). `win` null: the page's memory only.
 */
export function createUncertainRefs(win) {
  const mem = new Set();
  let storage = null;
  try {
    storage = (win && win.sessionStorage) || null;
  } catch {
    storage = null; // storage blocked: the page's memory only
  }
  const stored = () => {
    if (!storage) return [];
    try {
      const list = JSON.parse(storage.getItem(UNCERTAIN_REFS_KEY) || "[]");
      return Array.isArray(list) ? list.filter((r) => typeof r === "string" && APPROVAL_REF_PATTERN.test(r)) : [];
    } catch {
      return [];
    }
  };
  const write = (list) => {
    if (!storage) return;
    try {
      storage.setItem(UNCERTAIN_REFS_KEY, JSON.stringify(list.slice(-UNCERTAIN_REFS_MAX)));
    } catch {
      // full or blocked: the page's memory still has it
    }
  };
  return {
    has: (ref) => mem.has(ref) || stored().includes(ref),
    add(ref) {
      mem.add(ref);
      write([...stored().filter((r) => r !== ref), ref]);
    },
    delete(ref) {
      mem.delete(ref);
      const list = stored();
      if (list.includes(ref)) write(list.filter((r) => r !== ref));
    },
  };
}

/**
 * One add-to-cart per approval reference, even under a double click or a repeated `nc:approved`
 * message: a second call for a reference already in flight reuses the SAME promise, and one that
 * already SUCCEEDED is never repeated in this page. A failure clears the slot, so an explicit retry (a
 * new user action, never an automatic one) can try again. But a failed add may still have changed the
 * cart (audit 09-27 P1: its answer lost after Shopify committed it, a 5xx after the commit, or Shopify's
 * stock-limited 422, which adds what is in stock and still answers 422), so a reference is marked
 * uncertain (`uncertain`, createUncertainRefs) BEFORE its add is sent, and the mark is cleared only when
 * the add is known to have gone in whole. Every attempt for a marked reference runs with
 * `checkFirst: true` and reads the cart before it adds anything: a retry in this page, and, with the
 * tab's storage, one after a reload or an add still running when the page went away. Ported from the
 * old app's `createTestCartReceiver` (clickedcraft-customizer apps/editor/pilot/cart-bridge.mjs).
 */
export function createApprovalGate(uncertain = createUncertainRefs(null)) {
  const settled = new Map();
  return {
    /** `fn({ checkFirst })` → `{ ok, ... }`. */
    run(ref, fn) {
      const existing = settled.get(ref);
      if (existing) return existing;
      const checkFirst = uncertain.has(ref);
      uncertain.add(ref); // until this add is known to have gone in whole
      const p = Promise.resolve().then(() => fn({ checkFirst })).then(
        (result) => {
          if (result.ok) uncertain.delete(ref);
          else settled.delete(ref); // failed: a later explicit retry may try again, after reading the cart
          return result;
        },
        (err) => {
          settled.delete(ref);
          throw err;
        },
      );
      settled.set(ref, p);
      return p;
    },
  };
}

/** One cart line's identity as Shopify merges lines: its variant and its exact properties (any order). */
function cartLineKey(variantId, properties) {
  const props = properties || {};
  return `${variantId}|${JSON.stringify(Object.keys(props).sort().map((k) => [k, String(props[k])]))}`;
}

/**
 * What the cart (a GET /cart.js body) holds of ONE approval, measured against the lines `items`
 * (buildCartAddBody's) asked for. Only lines carrying this reference — `_design_ref` on the design
 * line, `_design_addon_of` on an add-on line — are counted; lines are grouped by variant + exact
 * properties, as Shopify merges them. Returns null when `cart` is not a cart it can read, else:
 * - `{ state: "complete" }`: every line is there at exactly the quantity asked for;
 * - `{ state: "absent" }`: none of it is there;
 * - `{ state: "partial", items }`: some of it is there, nothing more than asked for; `items` is exactly
 *   what is missing (a missing line, or the rest of a line Shopify capped at the stock it had);
 * - `{ state: "mismatch" }`: a line of this reference in a quantity or shape never asked for (more than
 *   asked, or properties that differ): nothing here can safely fix that, the buyer must look.
 */
export function reconcileCart(cart, approvalRef, items) {
  if (!cart || typeof cart !== "object" || !Array.isArray(cart.items)) return null;
  const want = new Map();
  for (const it of items) {
    const key = cartLineKey(it.id, it.properties);
    const g = want.get(key);
    if (g) g.quantity += it.quantity;
    else want.set(key, { id: it.id, quantity: it.quantity, properties: it.properties, ...(it.parent_id ? { parent_id: it.parent_id } : {}) });
  }
  const have = new Map();
  for (const line of cart.items) {
    if (!line || typeof line !== "object") return null;
    const props = line.properties && typeof line.properties === "object" ? line.properties : null;
    if (!props || (props[DESIGN_REF_PROPERTY] !== approvalRef && props[DESIGN_ADDON_OF_PROPERTY] !== approvalRef)) continue;
    const variantId = parseNumericId(line.variant_id ?? line.id);
    if (!variantId || !Number.isInteger(line.quantity) || line.quantity < 0) return null;
    const key = cartLineKey(variantId, props);
    if (!want.has(key)) return { state: "mismatch" };
    have.set(key, (have.get(key) || 0) + line.quantity);
  }
  let present = 0;
  const missing = [];
  for (const [key, g] of want) {
    const n = have.get(key) || 0;
    if (n > g.quantity) return { state: "mismatch" };
    if (n > 0) present++;
    if (n < g.quantity) {
      const item = { ...g, quantity: g.quantity - n };
      if (item.parent_id) {
        // A partial first add may already have inserted the parent. Shopify then
        // needs its exact cart-line key; parent_id only works when both lines are
        // being created in the same request.
        const parent = cart.items.find((line) =>
          parseNumericId(line?.variant_id ?? line?.id) === String(item.parent_id) &&
          line?.properties?.[DESIGN_REF_PROPERTY] === approvalRef);
        if (parent) {
          if (typeof parent.key !== "string" || !parent.key) return { state: "mismatch" };
          delete item.parent_id;
          item.parent_line_key = parent.key;
        }
      }
      missing.push(item);
    }
  }
  if (missing.length === 0) return { state: "complete" };
  return present === 0 ? { state: "absent" } : { state: "partial", items: missing };
}

/** GET the cart and reconcile it (reconcileCart); null when it cannot be read or understood. Never rejects. */
async function readCartFor(fetchImpl, cartUrl, approvalRef, items) {
  try {
    const res = await fetchImpl(cartUrl, { method: "GET", headers: { accept: "application/json" }, cache: "no-store" });
    if (!res.ok) return null;
    return reconcileCart(await res.json(), approvalRef, items);
  } catch {
    return null;
  }
}

const postJson = (body) => ({ method: "POST", headers: { "content-type": "application/json", accept: "application/json" }, body: JSON.stringify(body) });

/**
 * Adds an approved design to the cart. The first attempt for a reference is ONE `/cart/add.js` call
 * with buildCartAddBody's body, exactly as before; a 200 is success, with nothing read back. Never
 * rejects; never retries on its own: the buyer decides whether to try again.
 *
 * Any other outcome — a thrown fetch (its answer may be lost after Shopify committed the add), a 5xx,
 * any 4xx including Shopify's stock-limited 422 (it adds the most it has in stock and still answers 422:
 * shopify.dev/docs/api/ajax/reference/cart) — is followed by ONE read of the cart (GET `cartUrl`,
 * reconcileCart), and the result says what is actually there:
 * - `{ ok: true }` when all of it is in the cart after all;
 * - `{ ok: false, outcome: "not_added", message }` when none of it is (message: Shopify's description, or our code);
 * - `{ ok: false, outcome: "partial", message }` when only part of it is (a line missing, or capped by stock);
 * - `{ ok: false, outcome: "mismatch" }` when this reference is there in quantities never asked for;
 * - `{ ok: false, outcome: "unknown", message }` when the cart could not be read: nobody knows.
 * A later attempt for a reference whose add is not known to have gone in whole (the gate's `checkFirst`:
 * a failure, or an add still running when the page went away) reads the cart FIRST: complete → success
 * with no add; absent → the same whole body again; partial → only what is missing; mismatch or
 * unreadable → nothing is added.
 * Known limits (docs/STOREFRONT.md): the gate's memory is the page's plus the tab's sessionStorage, so an
 * attempt from another tab, a new visit, or a browser that refuses storage, after a reload, sends the
 * whole body without reading; and Shopify's cart API has no idempotency key, so an add that commits
 * AFTER the read that found the cart without it (a request still in flight on Shopify's side) cannot be
 * seen by any read.
 */
export async function addApprovedDesignToCart({ fetchImpl, cartAddUrl, cartUrl, approved, gate, quantity = 1 }) {
  const body = buildCartAddBody(approved, quantity);
  const ref = approved.approvalRef;
  return gate.run(ref, async ({ checkFirst }) => {
    let send = body;
    if (checkFirst) {
      const before = await readCartFor(fetchImpl, cartUrl, ref, body.items);
      if (!before) return { ok: false, outcome: "unknown", message: "cart_unreadable" };
      if (before.state === "complete") return { ok: true };
      if (before.state === "mismatch") return { ok: false, outcome: "mismatch" };
      if (before.state === "partial") send = { items: before.items };
    }
    let res = null;
    try {
      res = await fetchImpl(cartAddUrl, postJson(send));
    } catch {
      res = null; // the answer is lost; the add may or may not have happened
    }
    if (res && res.ok) return { ok: true };
    let message = res ? `cart_add_failed_${res.status}` : "network_error";
    if (res) {
      const answer = await res.json().catch(() => null);
      if (answer && typeof answer.description === "string" && answer.description) message = answer.description;
    }
    const after = await readCartFor(fetchImpl, cartUrl, ref, body.items);
    if (!after) return { ok: false, outcome: "unknown", message };
    if (after.state === "complete") return { ok: true };
    if (after.state === "absent") return { ok: false, outcome: "not_added", message };
    if (after.state === "partial") return { ok: false, outcome: "partial", message };
    return { ok: false, outcome: "mismatch", message };
  });
}

/**
 * The launcher's answer to an `nc:approved` (review finding 5): `{ v, type: "nc:cart-result", ok:
 * true }`, or `ok: false` with the buyer-facing `message` the popup shows next to a retry — or, with
 * `viewCart` (audit 09-27 P1: part of it, or maybe all of it, may be in the cart), next to a link to
 * the cart instead. The popup's zod schema (storefront-bridge.ts NcCartResult) accepts exactly this
 * shape (tests/unit/cart-result.test.ts).
 */
export function buildCartResultMessage(ok, message, viewCart = false) {
  if (ok) return { v: NC_PROTOCOL_VERSION, type: "nc:cart-result", ok: true };
  const out = { v: NC_PROTOCOL_VERSION, type: "nc:cart-result", ok: false, message: String(message).slice(0, 300) };
  if (viewCart) out.viewCart = true;
  return out;
}

/** What the buyer reads when /cart/add.js failed: Shopify's own description when it gave one
 * ("The product is already sold out."), a plain retry text for our own codes; at most 300 chars. */
export function cartFailureText(message) {
  const generic = "We couldn't add this to your cart. Please try again.";
  if (!message || /^[a-z0-9_]+$/.test(message)) return generic;
  return `We couldn't add this to your cart: ${message}`.slice(0, 300);
}

/** Outcomes after which the buyer must look at the cart before anything is added again. */
const LOOK_AT_CART = new Set(["partial", "mismatch", "unknown"]);
/** True when a failed add's result means "look at your cart" (a View cart link, never a plain retry). */
export function cartNeedsLook(added) {
  return !added.ok && LOOK_AT_CART.has(added.outcome);
}

/** The buyer's words for a failed add's result (addApprovedDesignToCart), at most 300 chars. */
export function cartOutcomeText(added) {
  const detail = added.message && !/^[a-z0-9_]+$/.test(added.message) ? ` ${added.message}` : "";
  if (added.outcome === "partial") return `Only part of this was added to your cart.${detail} Please check your cart.`.slice(0, 300);
  if (added.outcome === "mismatch") return "Your cart already has this design, but not in the amount asked for. Please check your cart.";
  if (added.outcome === "unknown") return "We couldn't tell whether this was added to your cart. Please check your cart before adding it again.";
  return cartFailureText(added.message);
}

/**
 * Where "go to the cart" should go after a successful add: the configured drawer trigger if the
 * block has one AND it is present on this page, else a plain navigation to `/cart` — which always
 * works and is the default, so an unconfigured or wrong selector never breaks the flow — including
 * one that is not valid CSS, on which querySelector throws (review 09-25).
 */
/** Shopify's locale root is a same-origin path, never an arbitrary destination. */
export function cartPath(win, suffix = "cart") {
  const root = win && win.Shopify && win.Shopify.routes && win.Shopify.routes.root;
  const safe = typeof root === "string" && /^\/(?:[a-zA-Z0-9-]+\/)*$/.test(root) ? root : "/";
  return safe + suffix;
}

export function chooseCartDestination(doc, drawerSelector, cartDestination = "/cart") {
  const trimmed = (drawerSelector || "").trim();
  if (trimmed) {
    let el = null;
    try {
      el = doc.querySelector(trimmed);
    } catch {
      el = null;
    }
    if (el) return { action: "click", el };
  }
  return { action: "navigate", url: cartDestination };
}

/** The popup's https origin from the block setting, or null (not configured). Plain http never
 * works: the storefront's CSP upgrades the iframe to https, so no message would match. */
export function popupOriginOf(raw) {
  const s = (raw || "").trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    return u.protocol === "https:" ? u.origin : null;
  } catch {
    return null;
  }
}

/** Beside the disabled button (its title, and a status line once the dialog is closed) while a cart add runs. */
export const ADDING_TEXT = "Adding your design to your cart…";

/** Under this media query the dialog is full screen. personalize.css holds the same text; a test
 * (tests/unit/storefront-block.test.ts) keeps the two identical. */
export const FULL_SCREEN_QUERY = "(max-width: 640px)";
const MIN_FRAME_PX = 120;

/**
 * The dialog's height limit in CSS px: its CSS max-height (`cssMaxPx`, read from the computed style;
 * NaN for `none`), never more than the visible viewport (`viewportPx`: visualViewport.height where
 * there is one, which on a phone excludes the on-screen keyboard).
 */
export function dialogMaxHeightPx({ cssMaxPx, viewportPx }) {
  return Number.isFinite(cssMaxPx) && cssMaxPx > 0 ? Math.min(cssMaxPx, viewportPx) : viewportPx;
}

/**
 * The iframe's height in CSS px. The dialog can be `dialogMaxPx` tall and also shows `chromePx` of
 * its own below the iframe (the error text), so the iframe gets at most the rest — never more, so the
 * dialog never cuts the popup off: the popup scrolls INSIDE the iframe, its actions pinned to the
 * bottom. Full screen (`fill`, phones) it gets all of that; otherwise the popup's content height
 * (`contentPx`, from nc:resize) when that is shorter, so a short popup is a short dialog.
 */
export function frameHeightPx({ contentPx, dialogMaxPx, chromePx, fill }) {
  const available = Math.max(MIN_FRAME_PX, Math.floor(dialogMaxPx - chromePx));
  if (fill || !(contentPx > 0)) return available;
  return Math.min(Math.ceil(contentPx), available);
}

/**
 * The dialog's top in CSS px on a phone, or null to leave it where personalize.css puts it. iOS Safari
 * does not shrink the page for the on-screen keyboard: it slides the visible part of the page over it
 * (visualViewport.offsetTop) to show the focused field, so a dialog fixed to the page's top slid up out
 * of view with it and the product page showed under it (Emre 2026-10-01, iPhone). A dialog as tall as the
 * visible viewport (`fill`, phones: the full-screen modal always, the sheet once the keyboard leaves it
 * less room than its own max height, `cssMaxPx`) follows it: its top is the visible viewport's top. Not
 * while the buyer has zoomed the page in (`scale`): then it stays where it is.
 */
/** On the popup's URL: this launcher keeps its dialog in the visible viewport (pinnedTopPx). The popup's
 *  typing look (a preview pinned at the top, the dock stepped aside) needs it. Under an older launcher (the
 *  extension before its release, the theme's vendored copy) the dialog slides up with the page, and the
 *  pinned preview took the field out of view with it (Emre 2026-10-01, live check after #75). */
export const PIN_PARAM = "pin";
export function pinnedTopPx({ fill, cssMaxPx, viewportPx, offsetTop, scale }) {
  if (!fill || !(viewportPx > 0)) return null;
  if (Number.isFinite(scale) && Math.abs(scale - 1) > 0.01) return null;
  if (Number.isFinite(cssMaxPx) && cssMaxPx > 0 && cssMaxPx < viewportPx - 1) return null; // shorter than the visible viewport: its own CSS anchor holds
  return Math.max(0, Math.round(offsetTop || 0));
}

// ---- The shell (docs/design/experience.md §1.1; docs/STOREFRONT.md "The shell"): the launcher opens the
// popup in the shell the product's published Storefront experience names — today's centred modal, a
// right-side drawer on desktop, a bottom sheet on phones, or "On the product page" (inline, below).
// It learns that shell from ONE read by product, GET /api/storefront/products/:productId/experience
// (src/server/routes/experiences.ts registerStorefrontExperienceRoute): the product's active link in OUR
// database names its family, so there is no second copy of that link on the page to drift from it.
// Except for an explicit data-nc-auto-inline root, nothing asks before buyer intent: hover, focus or tap starts the
// read, and the click itself if none came first. A click whose answer is not in yet shows a busy
// button and waits at most SHELL_WAIT_MS; then (or on a failed or malformed answer) it opens today's
// modal. Every such fallback is named (FALLBACK_REASONS), reported on the page (FALLBACK_EVENT and the
// console) AND counted on the server: the reason rides on the popup's URL (?fb=), and the popup's first
// launch request carries it to the server's count (src/experiences/storefront.ts, staff Health).
// The button's text is never touched: it is the block's own label setting, rendered by Liquid. ----

/** The most a click waits for the product's experience before today's modal opens (`shell_timeout`). */
export const SHELL_WAIT_MS = 1500;
/** How long the read itself may run before it is abandoned (`fetch_failed`); a click never waits this long. */
export const SHELL_FETCH_TIMEOUT_MS = 4000;

/**
 * Why the launcher opened today's modal instead of the shell asked for. The ONE list, in this order:
 * src/experiences/storefront.ts LAUNCHER_FALLBACK_REASONS must hold exactly these (a test keeps the two
 * identical), and the server counts each (an unknown code as "unrecognized").
 */
export const FALLBACK_REASONS = Object.freeze(["shell_timeout", "fetch_failed", "malformed", "not_linked", "inline_block_off", "load_failed", "load_timeout", "api_mismatch", "no_panel"]);

/** Dispatched on `document`, with `{ reason }`, each time the launcher opens a fallback shell. Also
 *  written to the console, and counted on the server through the popup URL's ?fb= (see above). */
export const FALLBACK_EVENT = "nc:launcher-fallback";

/** The shells each breakpoint can name (src/domain/experience/schema.ts DesktopShell / MobileShell). */
export const DESKTOP_SHELLS = Object.freeze(["modal", "drawer", "inline"]);
export const MOBILE_SHELLS = Object.freeze(["modal", "sheet", "inline"]);

/** The launcher's read: `${origin}/api/storefront/products/<productId>/experience`. */
export function productExperienceUrl(origin, productId) {
  return new URL(`/api/storefront/products/${encodeURIComponent(productId)}/experience`, origin).href;
}

/** The shell section of a 200 answer, or null when it is not `{ shell: { desktop, mobile } }` with a
 *  shell each breakpoint can name. */
export function parseShellAnswer(body) {
  const shell = body && typeof body === "object" ? body.shell : null;
  if (!shell || typeof shell !== "object") return null;
  if (!DESKTOP_SHELLS.includes(shell.desktop) || !MOBILE_SHELLS.includes(shell.mobile)) return null;
  return { desktop: shell.desktop, mobile: shell.mobile };
}

/**
 * GET the product's experience. Never rejects: `{ ok: true, shell }`, or `{ ok: false, reason }` with
 * `not_linked` (404 not_linked), `malformed` (a 200 parseShellAnswer refuses, or a body that arrived but
 * is not JSON) or `fetch_failed` (any other status, a network failure, a body that could not be read, or
 * SHELL_FETCH_TIMEOUT_MS passing — before the answer or during its body).
 * Never retried here: the caller decides.
 */
export async function fetchProductExperience({ fetchImpl, origin, productId, timers, timeoutMs = SHELL_FETCH_TIMEOUT_MS }) {
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timeoutId = controller && timers && typeof timers.setTimeout === "function" ? timers.setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    let res;
    try {
      res = await fetchImpl(productExperienceUrl(origin, productId), controller ? { signal: controller.signal } : {});
    } catch {
      return { ok: false, reason: "fetch_failed" };
    }
    let body = null;
    try {
      body = await res.json();
    } catch (e) {
      // A body that arrived but is not JSON (SyntaxError) is the answer's own shape: `malformed` on a
      // 200. A body that never arrived — the abort timeout firing mid-body, a dropped connection — is a
      // failed READ, not the server's answer: `fetch_failed`, asked again on the next intent or click.
      if (!(e && e.name === "SyntaxError")) return { ok: false, reason: "fetch_failed" };
    }
    if (res.status === 404 && body && body.error === "not_linked") return { ok: false, reason: "not_linked" };
    if (!res.ok) return { ok: false, reason: "fetch_failed" };
    const shell = parseShellAnswer(body);
    return shell ? { ok: true, shell } : { ok: false, reason: "malformed" };
  } finally {
    if (timeoutId != null && timers) timers.clearTimeout(timeoutId);
  }
}

/** "mobile" under the query the dialog goes full screen at (FULL_SCREEN_QUERY: one query, a test keeps it
 *  and personalize.css identical), "desktop" otherwise, including when `win` has no `matchMedia`. The ONE
 *  evaluator of the page's breakpoint: the shell, the inline gate and the class inline-panel.js tells the
 *  popup all read it. */
export function breakpointOf(win) {
  return typeof win.matchMedia === "function" && win.matchMedia(FULL_SCREEN_QUERY).matches ? "mobile" : "desktop";
}

/**
 * The shell to open, and the fallback reason when it is not the one asked for (null when it is). THE
 * gate, per breakpoint:
 * - no answer (`answer` null: SHELL_WAIT_MS passed) or a failed one: today's modal, counted by its reason;
 * - the experience says "inline": inline ONLY where the block's own setting for this breakpoint is on
 *   (`blockInline`, from data-nc-inline); where it is off, the modal, counted `inline_block_off`;
 * - any other shell: exactly that shell, whatever the block's inline setting says.
 * INLINE_ENABLED (src/domain/experience/check.ts) stays the one gate on PUBLISHING inline.
 */
export function launchShellOf({ answer, breakpoint, blockInline }) {
  if (!answer) return { shell: "modal", fallback: "shell_timeout" };
  if (!answer.ok) return { shell: "modal", fallback: answer.reason };
  const named = answer.shell[breakpoint];
  if (named === "inline") return blockInline.includes(breakpoint) ? { shell: "inline", fallback: null } : { shell: "modal", fallback: "inline_block_off" };
  return { shell: named, fallback: null };
}

/** The two shell classes personalize.css defines beyond today's plain modal. */
export const SHELL_DIALOG_CLASS = { drawer: "nc-personalize__dialog--drawer", sheet: "nc-personalize__dialog--sheet" };

/** Sets `dialogEl`'s shell class to match `shell`, removing the other one first (so a later re-open in a
 *  different shell never leaves two active). "modal" carries no class: today's markup, byte-for-byte. */
export function applyShellClass(dialogEl, shell) {
  dialogEl.classList.remove(SHELL_DIALOG_CLASS.drawer, SHELL_DIALOG_CLASS.sheet);
  const cls = SHELL_DIALOG_CLASS[shell];
  if (cls) dialogEl.classList.add(cls);
}

/** Beside the busy button while the click waits (for the product's experience, or for inline-panel.js). */
export const LOADING_TEXT = "Loading…";

// ---- "On the product page" (docs/design/experience.md §1.1): a block whose theme setting turns it on
// for a breakpoint (data-nc-inline, blocks/personalize.liquid) opens inline there when the product's
// experience ALSO says inline (launchShellOf). Then inline-panel.js, where all of its logic lives, loads —
// on the buyer's click, or for a root explicitly opted into data-nc-auto-inline after positive gates,
// and only when it will be used. No other block loads it. wireBlock only routes to it. ----

/** On a block that may open on the product page at some breakpoint ("desktop", "mobile" or both). */
export const INLINE_ATTR = "data-nc-inline";
/** The tallest popup content, in CSS px: the ONE bound on nc:resize and nc:scroll-to here, and the
 *  inline iframe's clamp. web/src/lib/storefront-bridge.ts FRAME_HEIGHT_MAX_PX is the popup's copy
 *  (tests/unit/launcher-inline.test.ts holds the two identical). */
export const FRAME_HEIGHT_MAX_PX = 10000;
/** The most a click keeps the button busy, counted FROM THE CLICK: whatever it waits for (the product's
 *  experience, at most SHELL_WAIT_MS of it, then inline-panel.js), a shell opens by then. ONE deadline,
 *  started at the click (clickDeadline) and handed to the inline load: inline-panel.js not in by then
 *  opens the pop-up instead (`load_timeout`), however long the experience read took before it. */
export const INLINE_LOAD_TIMEOUT_MS = 8000;
const INLINE_API = 1; // inline-panel.js's INLINE_API this launcher speaks
/** The ONE place inline-panel.js is fetched (an object, so tests/unit/launcher-inline.test.ts can count the loads). */
// Shopify caches unversioned modules for a year; follow the launcher asset revision.
// inline-panel.js revision 2026-10-26a (the design image swaps only once the new drawing is decoded: no blink while typing): change this note whenever inline-panel.js changes,
// so the theme's copied launcher.js gets a new asset revision and with it a fresh inline-panel.js URL.
export const inlineModule = { load: () => import(new URL("./inline-panel.js" + new URL(import.meta.url).search, import.meta.url).href) };

/**
 * On `<html>` while an inline panel holds the popup (audit 09-27 P2): the ids of those panels,
 * space-separated. The block's "Hide on the product page" CSS (blocks/personalize.liquid) applies only
 * under `html[data-nc-inline-active~="<its panel id>"]`, so the theme's option picker is hidden only
 * while the popup's own options replace it on the page: never on a product that opens a pop-up, a drawer
 * or a sheet, never after a fallback to the pop-up, never after the block is gone. Set when the panel
 * opens (on the buyer's click, as the reserved skeleton appears), kept while it is collapsed (the popup
 * still holds the buyer's choice), removed when it is released or the block is disposed.
 */
export const INLINE_ACTIVE_ATTR = "data-nc-inline-active";

/** Adds (`on`) or removes `token` in `<html>`'s INLINE_ACTIVE_ATTR list; the attribute goes when empty. */
export function markInlineActive(doc, token, on) {
  const html = doc.documentElement;
  const tokens = (html.getAttribute(INLINE_ACTIVE_ATTR) || "").split(/\s+/).filter((t) => t && t !== token);
  if (on) tokens.push(token);
  if (tokens.length) html.setAttribute(INLINE_ACTIVE_ATTR, tokens.join(" "));
  else html.removeAttribute(INLINE_ACTIVE_ATTR);
}

/** The breakpoints the block's own setting opens inline on (data-nc-inline): [] for every other block. */
export function blockInlineOf(root) {
  return root.hasAttribute(INLINE_ATTR) ? (root.getAttribute(INLINE_ATTR) || "").split(/\s+/).filter((b) => b === "desktop" || b === "mobile") : [];
}

/**
 * The block's inline state, or null for a block that never opens inline. `load(deadline)` starts the ONE
 * fetch of inline-panel.js and resolves (never rejects) once `panel` is set or `failed` names why it never
 * will be — at the latest when `deadline` (the click's, a promise) resolves: then `load_timeout`, and a
 * module arriving later is never used. `aria(on)`: the button is a disclosure of the panel (set once the
 * shell is known to be inline).
 */
function inlineShellOf(root, doc, win, parts) {
  if (!root.hasAttribute(INLINE_ATTR)) return null;
  const panelEl = root.querySelector("[data-nc-inline-panel]");
  const state = { panel: null, failed: null, loading: null };
  /** The picker-hiding mark on <html> (INLINE_ACTIVE_ATTR), by this block's panel id. */
  state.mark = (on) => {
    if (panelEl && panelEl.id) markInlineActive(doc, panelEl.id, on);
  };
  state.aria = (on) => {
    if (on && panelEl && panelEl.id) {
      if (!(state.panel && state.panel.isActive())) parts.button.setAttribute("aria-expanded", "false"); // the panel keeps it once it holds the popup
      parts.button.setAttribute("aria-controls", panelEl.id);
    } else {
      parts.button.removeAttribute("aria-expanded");
      parts.button.removeAttribute("aria-controls");
    }
  };
  state.load = (deadline) => {
    if (state.loading) return state.loading;
    const late = deadline.then(() => "load_timeout");
    const arrived = Promise.resolve().then(() => inlineModule.load()).then((m) => (m && m.INLINE_API === INLINE_API ? m : "api_mismatch"), () => "load_failed");
    state.loading = Promise.race([arrived, late]).then((m) => {
      if (typeof m === "string") { state.failed = m; return; } // a module arriving after the deadline is never used
      try {
        state.panel = m.createInlinePanel({ root, doc, win, ...parts, maxFramePx: FRAME_HEIGHT_MAX_PX, viewportNow: () => breakpointOf(win), version: NC_PROTOCOL_VERSION });
      } catch {
        state.panel = null;
      }
      if (!state.panel) state.failed = "no_panel";
    });
    return state.loading;
  };
  state.retry = () => {
    if (!state.panel) { state.loading = null; state.failed = null; }
  };
  return state;
}

// ---- The buy-button guard (launch audit 09-26: the theme's own "Add to bag" and its phone sticky bar
// added a personalized product with NO _design_ref). While a Personalize block for a product is on the
// page, every native way to add THAT product is stopped, fail-closed, and the buyer is sent to
// Personalize instead. Our own add is a fetch to /cart/add.js carrying _design_ref and is untouched;
// a form for any OTHER product (recommendations, add-ons) is untouched too. ----

/** What the buyer reads beside the Personalize button when a native buy path was stopped. */
export const USE_PERSONALIZE_TEXT = "This item is made just for you. Please use the Personalize button to create your design and add it to your cart.";
/** Shopify's dynamic checkout ("Buy it now", Shop Pay, Apple Pay…) — it checks out the form's variant
 *  directly, without any cart line of ours. personalize.css hides the same selectors inside a guarded
 *  form (they are injected after page load); a test keeps the two lists identical. */
export const DYNAMIC_CHECKOUT_SELECTOR = ".shopify-payment-button, shopify-accelerated-checkout, [data-shopify=\"payment-button\"]";
/** Sticky / floating add-to-cart bars (they always add the page's own product). personalize.css hides
 *  the same list on a page that holds a block, before any script runs; a test keeps the two identical. */
export const STICKY_BUY_SELECTOR = "cc-mobile-purchase, sticky-add-to-cart, sticky-atc, .sticky-atc, .sticky-add-to-cart, [data-sticky-add-to-cart], .product-sticky-form";
/** A theme buy control: a submit button (explicit type="submit"), the add button by its conventional
 *  name="add", an input submit or image submit. A type-less <button> is not one: themes use them for
 *  things like a size guide, whose own click must keep working (the submit guard stops the submit it
 *  causes). ONE list: isBuyControl matches it, and personalize.css hides it inside the form that holds a
 *  block from first paint, before launcher.js has run (a test keeps the two identical). */
export const BUY_CONTROL_SELECTOR = "button[type=\"submit\" i], button[name=\"add\"], input[type=\"submit\" i], input[type=\"image\" i]";
/** Set on a form that adds this product: personalize.css hides its dynamic checkout buttons. */
export const BUY_GUARD_ATTR = "data-nc-buy-guard";
/** Set on each buy control the guard hid (and removed with its inline display when the block goes). */
export const BUY_HIDDEN_ATTR = "data-nc-buy-hidden";

/** True when the point `(x, y)` falls outside `rect` (a `DOMRect`-shaped `{left, right, top, bottom}`).
 *  Used to tell a genuine <dialog> backdrop click from a click on the dialog element's OWN empty
 *  interior (review finding): both give `event.target === dialogEl`, since a native <dialog>'s
 *  `::backdrop` is not a DOM node and a click on it is reported with the dialog itself as the target —
 *  but only a backdrop click actually lands outside the dialog's own rendered box. */
export function isOutsideRect(rect, x, y) {
  return x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;
}

// ---- Everything below this line touches `document`/`window`. Nothing above it does, which is what
// keeps those functions testable under plain Node (tests/unit/launcher.test.ts). ----

/** A theme buy control (BUY_CONTROL_SELECTOR). */
function isBuyControl(el) {
  return !!el && typeof el.matches === "function" && el.matches(BUY_CONTROL_SELECTOR);
}

/** The variant a form would add: its `id` field (form.elements, so a field tied with form="…" counts). */
function formVariantId(form) {
  const field = form.elements && typeof form.elements.namedItem === "function" ? form.elements.namedItem("id") : null;
  return field ? parseNumericId(String(field.value || "").trim()) : null;
}

/** True when `form` adds this product: marked at wiring, holding the block, or its id is one of `variantIds`. */
function formAddsProduct(form, root, variantIds) {
  if (!form || form.tagName !== "FORM") return false;
  if (form.hasAttribute(BUY_GUARD_ATTR) || form.contains(root)) return true;
  const id = formVariantId(form);
  return !!id && variantIds.has(id);
}

// What the guard changed on the page, shared by every block (two blocks may guard the same form): the
// change is made by the first hold and undone by the last release, so one block's dispose never shows
// a button another block still hides.
const holds = new Map(); // `${kind}` → Map<Element, { count, undo }>
function hold(kind, el, apply) {
  let byEl = holds.get(kind);
  if (!byEl) holds.set(kind, (byEl = new Map()));
  let h = byEl.get(el);
  if (!h) byEl.set(el, (h = { count: 0, undo: apply(el) }));
  h.count++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--h.count === 0) {
      byEl.delete(el);
      h.undo();
    }
  };
}
/** Hides `el` with an inline `display: none !important` (beats the theme's own rules and its scripts'
 *  `hidden` toggling); undo restores its exact style attribute. */
function hideBuyControl(el) {
  const before = el.getAttribute("style");
  el.style.setProperty("display", "none", "important");
  el.setAttribute(BUY_HIDDEN_ATTR, "");
  return () => {
    if (before === null) el.removeAttribute("style");
    else el.setAttribute("style", before);
    el.removeAttribute(BUY_HIDDEN_ATTR);
  };
}
function markGuardedForm(form) {
  form.setAttribute(BUY_GUARD_ATTR, "");
  return () => form.removeAttribute(BUY_GUARD_ATTR);
}
/** form.submit() posts the form with NO submit event and no click (HTML spec), so neither capture guard
 *  sees it; jQuery's .submit() / .trigger("submit") ends in the same call. A guarded form gets its own
 *  `submit` that sends the buyer to Personalize instead; undo puts back exactly what was there (the
 *  prototype's, or a theme's own replacement).
 *  A form holding a control named "submit" already has no callable form.submit() (the control shadows
 *  it), so there is nothing to replace. Still not stopped: HTMLFormElement.prototype.submit.call(form). */
function shadowFormSubmit(sendToPersonalize) {
  return (form) => {
    const own = Object.getOwnPropertyDescriptor(form, "submit"); // a theme's own replacement, kept for undo
    if (typeof form.submit !== "function" || (own && !own.configurable)) return () => {};
    Object.defineProperty(form, "submit", { configurable: true, writable: true, value: () => sendToPersonalize() });
    return () => {
      if (own) Object.defineProperty(form, "submit", own);
      else delete form.submit;
    };
  };
}
// The launcher's own click on the configured cart-drawer trigger is never stopped by a guard.
let launcherClicking = false;

/**
 * Stops every native way to add this product while `root` (its Personalize block) is on the page:
 * - hides the buy buttons and dynamic checkout of each form that adds it, and any sticky add-to-cart bar;
 * - a capture-phase click guard: a buy control of such a form, its dynamic checkout, or a sticky bar;
 * - a capture-phase submit guard: any submit of such a form (Enter in a field, a type-less button,
 *   requestSubmit, the theme's own script clicking its hidden button);
 * - such a form's own submit() (shadowFormSubmit), which fires no event for either guard to see.
 * `listen` and `cleanups` are the block's, so its dispose undoes all of it. `sendToPersonalize` tells
 * the buyer where to go instead.
 */
function guardNativeBuy({ root, doc, variantIds, listen, cleanups, sendToPersonalize }) {
  const ours = (el) => !!(el && typeof el.closest === "function" && el.closest("[data-nc-personalize]"));
  for (const form of doc.querySelectorAll("form")) {
    if (!formAddsProduct(form, root, variantIds)) continue;
    cleanups.push(hold("mark", form, markGuardedForm));
    cleanups.push(hold("submit", form, shadowFormSubmit(sendToPersonalize)));
    // form.elements also holds a control tied with form="…" from outside; it never holds an image
    // submit (HTML spec), which querySelectorAll finds inside the form.
    const controls = new Set([...(form.elements || [])].filter(isBuyControl));
    for (const el of form.querySelectorAll(BUY_CONTROL_SELECTOR)) controls.add(el);
    for (const el of [...controls, ...form.querySelectorAll(DYNAMIC_CHECKOUT_SELECTOR)]) {
      if (!ours(el)) cleanups.push(hold("hide", el, hideBuyControl));
    }
  }
  for (const el of doc.querySelectorAll(STICKY_BUY_SELECTOR)) {
    if (!ours(el)) cleanups.push(hold("hide", el, hideBuyControl));
  }
  const stop = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    sendToPersonalize();
  };
  listen(doc, "click", (event) => {
    const t = event.target;
    if (launcherClicking || !t || typeof t.closest !== "function" || ours(t)) return;
    if (t.closest(STICKY_BUY_SELECTOR)) return stop(event);
    const checkout = t.closest(DYNAMIC_CHECKOUT_SELECTOR);
    // Outside any form it is the page's own product (a dynamic checkout for another product lives in that product's form).
    if (checkout) {
      const form = checkout.closest("form");
      if (!form || formAddsProduct(form, root, variantIds)) return stop(event);
      return;
    }
    const control = t.closest("button, input");
    if (isBuyControl(control) && formAddsProduct(control.form, root, variantIds)) stop(event);
  }, true);
  listen(doc, "submit", (event) => {
    if (formAddsProduct(event.target, root, variantIds)) stop(event);
  }, true);
}

/** The product's variant ids, rendered by the block (data-variant-ids); null when absent. */
export function productVariantIds(root) {
  const ids = (root.dataset.variantIds || "").split(",").map((v) => parseNumericId(v.trim())).filter(Boolean);
  return ids.length ? new Set(ids) : null;
}

function productForm(root, doc) {
  return root.closest("form") || doc.querySelector('form[action*="/cart/add"]');
}

function currentVariantId(root, doc) {
  const form = productForm(root, doc);
  const input = form ? form.querySelector('[name="id"]') : null;
  return (input && parseNumericId(input.value)) || root.dataset.variantId || null;
}

/** The product page's quantity (parseQuantity): 1 when the page has no quantity field, null when its value is not one. */
function currentQuantity(root, doc) {
  const input = quantityInputOf(productForm(root, doc), doc);
  return parseQuantity(input ? input.value : null);
}

// No variant watch: openDialog reads the variant at click time (review 09-25).

// The theme editor re-renders a section by replacing its HTML and never re-runs a module script, so
// a block is wired at most once, `wired` holds the dispose that undoes everything it started, and
// startLauncher follows the editor's events (review 09-25).
const wired = new WeakMap();
const wiredRoots = new Set(); // the same blocks, listable

/** Disposes every wired block inside `scope` (or `scope` itself), and every one no longer in the page. */
function disposeWithin(scope) {
  for (const root of [...wiredRoots]) {
    if (!root.isConnected || (scope && (scope === root || (typeof scope.contains === "function" && scope.contains(root))))) wired.get(root)();
  }
}

function wireBlock(root, doc, win) {
  if (wired.has(root)) return; // wired already: a second listener set would add to the cart twice
  const cleanups = [];
  /** addEventListener, undone by this block's dispose. */
  const listen = (target, type, fn, options) => {
    target.addEventListener(type, fn, options);
    cleanups.push(() => target.removeEventListener(type, fn, options));
  };
  wired.set(root, () => {
    wired.delete(root);
    wiredRoots.delete(root);
    for (const undo of cleanups.splice(0).reverse()) undo();
  });
  wiredRoots.add(root);

  const button = root.querySelector("[data-nc-open]");
  const dialogEl = root.querySelector("[data-nc-dialog]");
  const iframe = root.querySelector("[data-nc-iframe]");
  const closeBtn = root.querySelector("[data-nc-close]");
  const loadingEl = root.querySelector("[data-nc-loading]");
  const statusEl = root.querySelector("[data-nc-status]");
  const errorEl = root.querySelector("[data-nc-error]");
  const retryBtn = root.querySelector("[data-nc-retry]");
  // Beside the button, OUTSIDE the dialog: what stops the dialog from opening is shown where the
  // buyer is looking (a message inside a closed dialog is never seen).
  const pageErrorEl = root.querySelector("[data-nc-page-error]");
  // Beside it too: what became of a cart add whose dialog was closed while it ran (role=status).
  const pageStatusEl = root.querySelector("[data-nc-page-status]");
  if (!button || !dialogEl || !iframe || !closeBtn) return;

  // The buy-button guard comes first, before any "not ready" return: a block that cannot open (no
  // origin, too many variants) still stops the theme from adding this product without a design —
  // fail closed. Only a block with no variant list (not a product page) cannot tell which product to guard.
  const guardedVariants = productVariantIds(root);
  if (guardedVariants) {
    guardNativeBuy({
      root, doc, variantIds: guardedVariants, listen, cleanups,
      sendToPersonalize() {
        showPageStatus("");
        showPageError(USE_PERSONALIZE_TEXT);
        if (typeof button.scrollIntoView === "function") button.scrollIntoView({ block: "center" });
        if (typeof button.focus === "function") button.focus({ preventScroll: true });
      },
    });
  }

  const expectedOrigin = popupOriginOf(root.dataset.popupOrigin);
  // Until the block has an origin, a product and its variant list, the button stays disabled, saying why.
  const notReady = !expectedOrigin ? "Personalization is not configured for this block yet."
    : !parseNumericId(root.dataset.productId) || !productVariantIds(root) ? "Personalization is only available on a product page."
    : null;
  if (notReady) {
    button.disabled = true;
    button.title = notReady;
    if (root.hasAttribute("data-nc-auto-inline") && pageErrorEl) {
      root.setAttribute("data-nc-auto-state", "error");
      pageErrorEl.textContent = notReady;
      pageErrorEl.hidden = false;
      return;
    }
    if (!expectedOrigin) {
      // An origin Liquid let through but that is no https URL (a typo): buyers get nothing, never a
      // dead button; staff in the theme editor get the hint Liquid renders there (review 09-25).
      const hint = root.querySelector("[data-nc-editor-hint]");
      if (hint) hint.hidden = false;
      else root.hidden = true;
    }
    return;
  }
  button.disabled = false;
  // "On the product page" (inline-panel.js), for a block set to it; null for every other block.
  const inline = inlineShellOf(root, doc, win, { button, iframe, frameWrap: iframe.parentNode, statusEl, listen, popupOrigin: expectedOrigin });
  const inlineActive = () => !!(inline && inline.panel && inline.panel.isActive());
  const autoInline = root.hasAttribute("data-nc-auto-inline");
  const priceEl = root.hasAttribute("data-nc-price-target") ? root.closest("[data-cc-product]")?.querySelector("[data-cc-price]") : null;
  const originalPrice = priceEl?.textContent ?? null;
  if (priceEl) cleanups.push(() => { priceEl.textContent = originalPrice; });
  if (autoInline) button.disabled = true; // no manual modal while the page's inline setup is pending

  const blockInline = blockInlineOf(root);

  // The shell comes from ONE read by product (fetchProductExperience), started by the first sign of
  // intent — hover, focus or tap — or by the click itself; never on page load, never on a timer. An
  // answer that stands (a shell, not_linked, malformed) is kept for this page; a failed read is not, so
  // the next intent or click asks again.
  let shellRead = null; // the read in flight, or its settled answer's promise
  let shellAnswer = null; // an answer that stands for this page view
  function startShellRead() {
    if (shellRead) return shellRead;
    const read = fetchProductExperience({ fetchImpl: win.fetch.bind(win), origin: expectedOrigin, productId: root.dataset.productId, timers: win }).then((answer) => {
      if (answer.ok || answer.reason !== "fetch_failed") shellAnswer = answer;
      else if (shellRead === read) shellRead = null;
      if (inline && wired.has(root)) inline.aria(launchShellOf({ answer, breakpoint: breakpointOf(win), blockInline }).shell === "inline");
      return answer;
    });
    shellRead = read;
    return read;
  }
  listen(button, "pointerenter", startShellRead);
  listen(button, "focus", startShellRead);
  listen(button, "touchstart", startShellRead, { passive: true });

  /** Names a fallback where it can be seen on the page (FALLBACK_EVENT, the console); the server counts it
   *  from the popup URL's ?fb= (loadPopup). */
  function reportFallback(reason) {
    if (typeof win.CustomEvent === "function") doc.dispatchEvent(new win.CustomEvent(FALLBACK_EVENT, { detail: { reason } }));
    console.warn(`Personalize: opened the pop-up instead of the shell asked for (${reason}).`);
  }

  // The click's wait (for the product's experience, then for inline-panel.js): the button is busy and says
  // so beside it; a second click meanwhile does nothing. `ms`: the most it waits (then `then(null)`).
  let waiting = false;
  let cancelWait = null;
  function waitThen(promise, ms, then) {
    waiting = true;
    button.setAttribute("aria-busy", "true");
    showPageStatus(LOADING_TEXT);
    let timer = null;
    const late = ms === null ? [] : [new Promise((resolve) => { timer = win.setTimeout(() => resolve(null), ms); })];
    cancelWait = () => { if (timer !== null) win.clearTimeout(timer); };
    Promise.race([promise, ...late]).then((result) => {
      cancelWait();
      cancelWait = null;
      waiting = false;
      if (!wired.has(root)) return; // disposed while it waited (its section left the editor)
      button.removeAttribute("aria-busy");
      showPageStatus("");
      then(result);
    });
  }
  cleanups.push(() => { if (cancelWait) cancelWait(); });

  const gate = createApprovalGate(createUncertainRefs(win)); // the tab remembers an uncertain add across a reload
  const cartAddUrl = new URL(cartPath(win, "cart/add.js"), win.location.origin).href;
  const cartUrl = new URL(cartPath(win, "cart.js"), win.location.origin).href; // read back only after an add whose outcome is not known

  // The popup's content height (nc:resize); 0 until it reports one.
  let contentPx = 0;
  const isOpen = () => dialogEl.open === true || dialogEl.hasAttribute("open");
  /** Size the iframe to the space the dialog has right now (frameHeightPx), and on a phone keep the
   * dialog in the visible viewport (pinnedTopPx): on open, on every nc:resize, when the error text
   * appears or goes, and when the window or the visible viewport (a phone's keyboard) changes or moves. */
  function fitFrame() {
    if (!isOpen()) return;
    const vv = win.visualViewport;
    const viewportPx = (vv && vv.height) || win.innerHeight;
    const cssMaxPx = parseFloat(win.getComputedStyle(dialogEl).maxHeight);
    const fill = typeof win.matchMedia === "function" && win.matchMedia(FULL_SCREEN_QUERY).matches;
    // Everything the dialog shows below the iframe (the error text and its retry button).
    const chromePx = statusEl ? statusEl.getBoundingClientRect().height : errorEl && !errorEl.hidden ? errorEl.getBoundingClientRect().height : 0;
    iframe.style.height = `${frameHeightPx({ contentPx, dialogMaxPx: dialogMaxHeightPx({ cssMaxPx, viewportPx }), chromePx, fill })}px`;
    pinTop(pinnedTopPx({ fill, cssMaxPx, viewportPx, offsetTop: vv ? vv.offsetTop : 0, scale: vv ? vv.scale : 1 }));
  }
  /** The visible viewport's top edge in the coordinates getBoundingClientRect() answers in. Read from the page, not
   * assumed: iOS 26.5 Safari answers boxes relative to the VISIBLE viewport (this is 0 there with the keyboard up,
   * and a fixed box at top 0 reads -offsetTop: taking offsetTop off it once more placed the dialog twice as far
   * down, 10-09), other browsers relative to the page's own viewport (then it is offsetTop). Both follow from the
   * visible viewport's top in the page (pageTop) less where the page itself starts in those coordinates. Without
   * pageTop: the page's own viewport is assumed. */
  function visibleTopInBoxCoords() {
    const vv = win.visualViewport;
    if (!vv) return 0;
    const rootEl = dialogEl.ownerDocument.documentElement;
    if (!Number.isFinite(vv.pageTop) || !rootEl) return vv.offsetTop || 0;
    return vv.pageTop + rootEl.getBoundingClientRect().top - (parseFloat(win.getComputedStyle(rootEl).marginTop) || 0);
  }
  /** The dialog's top in px (its bottom then follows its height), or null: where personalize.css puts it. */
  function pinTop(topPx) {
    if (topPx === null) {
      dialogEl.style.removeProperty("top");
      dialogEl.style.removeProperty("bottom");
      return;
    }
    // Move it by what is still missing, measured where it really is: never by visualViewport.offsetTop on trust.
    // Two Safaris, two answers. iOS 26.5 (measured 10-09, simulator, address bar at the top) lays a fixed dialog out
    // offsetTop above the visible viewport, so it needs offsetTop added. Seray's iPhone (10-08) already keeps it in
    // the visible viewport, and adding offsetTop pushed the popup below the keyboard: blank, then jumping as Safari
    // chased the field. While the dialog is transformed (its opening animation) its box is not its place: then the
    // target as is.
    let next = topPx;
    const style = win.getComputedStyle(dialogEl);
    if (style.transform === "none" || style.transform === "") {
      const now = parseFloat(dialogEl.style.top || style.top);
      const seenTop = dialogEl.getBoundingClientRect().top - visibleTopInBoxCoords(); // its top in the visible viewport
      if (Number.isFinite(now) && Number.isFinite(seenTop)) next = Math.max(0, Math.round(now - seenTop));
    }
    dialogEl.style.top = `${next}px`;
    dialogEl.style.bottom = "auto"; // the sheet is anchored by its bottom: the top alone places it now
  }
  listen(win, "resize", fitFrame);
  if (win.visualViewport) {
    listen(win.visualViewport, "resize", fitFrame);
    listen(win.visualViewport, "scroll", fitFrame); // iOS slides the visible viewport to the focused field
  }

  /** The dialog's error text, and (`retry`) the button that loads the popup again. */
  function showError(message, retry = false) {
    if (errorEl) {
      errorEl.textContent = message || "";
      errorEl.hidden = !message;
    }
    if (retryBtn) retryBtn.hidden = !(message && retry);
    fitFrame(); // the text takes room below the iframe: never push it out of the dialog
  }
  function setLoading(isLoading) {
    if (loadingEl) loadingEl.hidden = !isLoading;
    iframe.hidden = isLoading;
  }

  // No nc:ready in time (the popup failed to load, or could not reach this page): say so and offer a
  // retry, never an endless "Loading…".
  const readyWatch = createReadyWatch(win, READY_TIMEOUT_MS, () => {
    if (loadingEl) loadingEl.hidden = true;
    showError("This is taking longer than it should. Please check your connection and try again.", true);
  });
  /** `message` in `el`; `cartLink`: followed by a link to the cart. */
  function showLine(el, message, cartLink) {
    if (!el) return;
    el.textContent = message || "";
    if (message && cartLink) {
      const link = doc.createElement("a");
      link.href = cartPath(win);
      link.textContent = "View cart";
      el.append(" ", link);
    }
    el.hidden = !message;
  }
  function showPageError(message, cartLink = false) {
    showLine(pageErrorEl, message, cartLink);
  }
  function showPageStatus(message, cartLink = false) {
    showLine(pageStatusEl, message, cartLink);
  }

  // Scroll lock (accessibility: every shell, not only the modal — a drawer or sheet leaves page content
  // visible beside/behind it, so without this the page behind it would stay scrollable). This is a real
  // behaviour change for EVERY dialog shell, today's modal included: native <dialog> top-layer
  // behaviour does NOT reliably stop wheel-scrolling the page behind an open dialog (review finding:
  // verified against Chrome, Firefox and Safari), so this lock is not a redundant belt-and-braces — it is
  // the only thing that stops it. personalize.css reserves the scrollbar's own width
  // (`scrollbar-gutter: stable`) on any page holding a block, so removing the scrollbar here never
  // shifts the page sideways (review finding: it did, on Windows/Linux with classic scrollbars).
  let releaseScrollLock = null;
  function lockScroll() {
    if (releaseScrollLock) return;
    const html = doc.documentElement;
    const before = html.style.overflow;
    html.style.overflow = "hidden";
    releaseScrollLock = () => {
      html.style.overflow = before;
      releaseScrollLock = null;
    };
  }

  let launchVariant = null;
  let launchQuantity = 1;
  // The ACTIVE launch (newLaunchId), or null: every load of the popup starts a new one; closing the dialog,
  // releasing the inline panel and disposing the block end it. Only its popup's messages are heard, and a
  // cart add that ends in a later launch (or none) is for a popup the buyer no longer has.
  let launchId = null;
  cleanups.push(() => { launchId = null; });
  const adding = new Set(); // approval references whose cart add is running (a repeated nc:approved shares one)
  const delivered = new WeakSet(); // add results already acted on in their own launch
  // Why this popup opens in a fallback shell (a FALLBACK_REASONS code), or null: on its URL as ?fb=.
  let launchFallback = null;
  function loadPopup() {
    showError("");
    setLoading(true);
    contentPx = 0;
    launchId = newLaunchId(win.crypto);
    const look = readHostLook(root, win);
    // The dialog around the popup takes the page's background and corners too (personalize.css).
    if (look && look.bg) dialogEl.style.setProperty("--nc-page-bg", look.bg);
    if (look && look.btn && /^(0|\d{1,2}(\.\d+)?px)$/.test(look.btn.radius)) dialogEl.style.setProperty("--nc-dialog-radius", look.btn.radius);
    const launchUrl = new URL(buildPopupUrl(expectedOrigin, root.dataset.productId, launchVariant, win.location.origin, launchFallback, launchId, look));
    launchUrl.searchParams.set("cartPath", cartPath(win));
    launchUrl.searchParams.set(PIN_PARAM, "1");
    const url = launchUrl.href;
    iframe.src = inlineActive() ? inline.panel.popupUrl(url) : url;
    readyWatch.start();
  }

  /** The click's ONE deadline, INLINE_LOAD_TIMEOUT_MS after it: `at` resolves then; `stop()` once the click
   *  no longer waits (and on dispose). Every wait of one click ends by it, whatever came first. */
  let stopDeadline = null;
  function clickDeadline() {
    let timer = null;
    const at = new Promise((resolve) => { timer = win.setTimeout(resolve, INLINE_LOAD_TIMEOUT_MS); });
    const stop = () => {
      win.clearTimeout(timer);
      if (stopDeadline === stop) stopDeadline = null;
    };
    stopDeadline = stop;
    return { at, stop };
  }
  cleanups.push(() => { if (stopDeadline) stopDeadline(); });

  /** The click: the shell comes from the product's experience (launchShellOf). An answer already in opens
   *  at once; otherwise the button is busy for at most SHELL_WAIT_MS, then the answer (or today's modal).
   *  The whole click — that wait and any wait for inline-panel.js after it — ends by ONE deadline. */
  function openDialog() {
    if (waiting) return;
    const deadline = clickDeadline();
    const decide = (answer) => launchShellOf({ answer, breakpoint: breakpointOf(win), blockInline });
    if (shellAnswer) openIn(decide(shellAnswer), deadline);
    else waitThen(startShellRead(), SHELL_WAIT_MS, (answer) => openIn(decide(answer), deadline));
  }

  /** Opens `decision.shell`. Inline needs inline-panel.js first (the button stays busy while it loads,
   *  until the click's `deadline` at most); when it cannot be had, the modal opens instead, counted by its
   *  reason. */
  function openIn(decision, deadline, auto = false) {
    let { shell, fallback } = decision;
    if (shell === "inline") {
      if (!inline) { shell = "modal"; fallback = "no_panel"; } // unreachable: blockInline and `inline` read the same attribute
      else if (!inline.panel && !inline.failed) { waitThen(inline.load(deadline.at), null, () => openIn(decision, deadline)); return; }
      else if (inline.failed) { shell = "modal"; fallback = inline.failed; }
    }
    deadline.stop(); // this opening waits no more
    if (auto && shell !== "inline") { showAutoFailure("Customization could not open on this page."); return; }
    if (inline) {
      if (shell !== "inline" && inline.panel && inline.panel.isActive()) {
        inline.panel.release(); // the iframe goes back into the dialog; its popup's launch ends here
        inline.mark(false); // and the theme's picker is shown again
        launchId = null;
      }
      inline.aria(shell === "inline");
    }
    const panel = shell === "inline" ? inline.panel : null;
    if (panel && panel.isExpanded()) { panel.collapse(); return; } // the button toggles the panel
    // product.variants stops at 250: say so now, not as a refused approval at the very end.
    if (root.hasAttribute("data-variants-truncated")) {
      const message = "This product has too many options to personalize here. Please contact us to order it.";
      if (auto) showAutoFailure(message); else showPageError(message);
      return;
    }
    const v = currentVariantId(root, doc);
    if (!v) {
      if (auto) showAutoFailure("Please choose an option before personalizing.");
      else showPageError("Please choose an option before personalizing.");
      return;
    }
    // The page's quantity goes on every cart line of this design (review finding 4); a value that
    // is not a quantity is asked for, never guessed.
    const q = currentQuantity(root, doc);
    if (q === null) {
      if (auto) showAutoFailure("Please enter a quantity from 1 to 9999 before personalizing.");
      else showPageError("Please enter a quantity from 1 to 9999 before personalizing.");
      return;
    }
    showPageError("");
    showPageStatus("");
    if (panel && panel.hasPopup() && v === launchVariant) { launchQuantity = q; panel.expand(); return; } // as the buyer left it
    launchVariant = v;
    launchQuantity = q;
    launchFallback = fallback;
    if (fallback) reportFallback(fallback);
    // Inline first: a panel on the page never gets a dialog shell class and never locks page scroll. The
    // theme's picker is hidden from now on (the popup's options replace it), as the skeleton is reserved.
    if (panel) {
      panel.open({ focus: !auto }); inline.mark(true); loadPopup();
      if (auto) { root.setAttribute("data-nc-auto-state", "ready"); button.disabled = false; }
      return;
    }
    applyShellClass(dialogEl, shell);
    loadPopup();
    if (typeof dialogEl.showModal === "function") dialogEl.showModal();
    else dialogEl.setAttribute("open", ""); // no native <dialog>: still opens, just without a focus trap
    lockScroll();
    fitFrame();
  }
  function closeDialog() {
    launchId = null;
    readyWatch.stop();
    if (typeof dialogEl.close === "function") dialogEl.close();
    else dialogEl.removeAttribute("open");
    pinTop(null);
    iframe.src = "about:blank";
    if (releaseScrollLock) releaseScrollLock();
    // Closed while a cart add runs: the disabled button says why, where the buyer now is.
    if (adding.size > 0) showPageStatus(ADDING_TEXT);
  }

  listen(button, "click", openDialog);
  function showAutoFailure(message) {
    root.setAttribute("data-nc-auto-state", "error");
    button.disabled = true;
    showPageStatus("");
    showPageError(message);
    if (!pageErrorEl) return;
    const retry = doc.createElement("button");
    retry.type = "button";
    retry.setAttribute("data-nc-auto-retry", "");
    retry.textContent = "Retry customization";
    retry.addEventListener("click", () => { void autoOpen(true); });
    pageErrorEl.append(" ", retry);
  }
  async function autoOpen(retry = false) {
    if (!wired.has(root)) return;
    if (retry) { shellRead = null; shellAnswer = null; if (inline) inline.retry(); }
    root.setAttribute("data-nc-auto-state", "loading");
    button.disabled = true;
    showPageError("");
    showPageStatus(LOADING_TEXT);
    const breakpoint = breakpointOf(win);
    if (!inline || !blockInline.includes(breakpoint)) {
      showAutoFailure("Customization is not configured for this page.");
      return;
    }
    const answer = await startShellRead();
    if (!wired.has(root)) return;
    if (!answer.ok || answer.shell[breakpoint] !== "inline") {
      showAutoFailure("Customization could not open on this page.");
      return;
    }
    const deadline = clickDeadline();
    await inline.load(deadline.at);
    if (!wired.has(root)) { deadline.stop(); return; }
    const currentBreakpoint = breakpointOf(win);
    if (!blockInline.includes(currentBreakpoint) || answer.shell[currentBreakpoint] !== "inline") {
      deadline.stop();
      showAutoFailure("Customization is not configured for this page.");
      return;
    }
    if (!inline.panel) {
      deadline.stop();
      showAutoFailure("Customization could not load. Please try again.");
      return;
    }
    openIn({ shell: "inline", fallback: null }, deadline, true);
  }
  if (autoInline) void autoOpen();
  if (retryBtn) listen(retryBtn, "click", loadPopup);
  listen(closeBtn, "click", closeDialog);
  listen(dialogEl, "cancel", closeDialog); // native <dialog>: fires on Escape
  listen(dialogEl, "click", (e) => {
    // `e.target === dialogEl` alone fires both for a genuine backdrop click AND for a click on dialogEl's
    // OWN empty interior (review finding: the drawer/sheet's box is fixed at full height while its
    // content — the "Loading…" frame-wrap, or the error text — is much shorter, so most of a drawer or
    // sheet is exactly this: dialogEl's own background, not a backdrop). isOutsideRect tells them apart.
    if (e.target === dialogEl && isOutsideRect(dialogEl.getBoundingClientRect(), e.clientX, e.clientY)) closeDialog();
  });
  // Disposed (its section unloaded in the editor): no ready watch left to fire, no dialog left open, the
  // theme's picker no longer hidden for a panel that is gone.
  cleanups.push(() => {
    readyWatch.stop();
    if (isOpen()) closeDialog();
    if (inline) inline.mark(false);
    if (inline && inline.panel && typeof inline.panel.dispose === "function") inline.panel.dispose();
  });

  listen(win, "message", (event) => {
    if (event.origin !== expectedOrigin || event.source !== iframe.contentWindow) return;
    const data = event.data;
    if (!data || typeof data !== "object" || data.v !== NC_PROTOCOL_VERSION) return;
    // Only the ACTIVE launch's popup is heard (audit 09-27 P2): a message queued by a popup the buyer
    // closed, the one before a reopen or a Retry, or one of a disposed block, is dropped here, before it
    // can close, resize or add anything. The origin and frame checks above cannot tell those apart: the
    // iframe's window stays the same object across its loads.
    if (launchId === null || data.launch !== launchId) return;
    if (data.type === "nc:ready") {
      readyWatch.ready();
      showError(""); // a late nc:ready clears the timeout message
      setLoading(false);
      if (inlineActive()) inline.panel.popupReady();
      return;
    }
    if (data.type === "nc:resize") {
      if (inlineActive()) { inline.panel.resize(data.heightPx); return; }
      if (typeof data.heightPx === "number" && data.heightPx > 0 && data.heightPx <= FRAME_HEIGHT_MAX_PX) {
        contentPx = data.heightPx;
        fitFrame();
      }
      return;
    }
    if (data.type === "nc:close") {
      if (inlineActive()) inline.panel.collapse();
      else closeDialog();
      return;
    }
    if (data.type === "nc:scroll-to") {
      if (inlineActive()) inline.panel.scrollTo(data);
      return;
    }
    if (data.type === "nc:preview") {
      if (inlineActive() && typeof inline.panel.preview === "function") inline.panel.preview(data);
      return;
    }
    if (data.type === "nc:typing") {
      if (inlineActive() && typeof inline.panel.typing === "function") inline.panel.typing(data);
      return;
    }
    if (data.type === "nc:proof-preview") {
      if (!root.hasAttribute("data-nc-direct-cart") || !inlineActive()) return;
      const proofId = data.proofId, sha = data.displaySha256;
      if (typeof proofId !== "string" || !/^pf_[A-Za-z0-9_-]{22}$/.test(proofId) ||
          !(sha === null || typeof sha === "string" && /^[0-9a-f]{64}$/.test(sha))) return;
      const path = `/api/proofs/${proofId}${sha ? `/display/${sha}` : ""}`;
      if (data.url !== path) return; // no other host, route, query or proof's display copy
      const thisLaunch = launchId;
      const answer = (shown) => {
        if (launchId !== thisLaunch || !inlineActive()) return;
        iframe.contentWindow.postMessage({ v: NC_PROTOCOL_VERSION, type: "nc:proof-shown", proofId,
          displaySha256: sha, shown, launch: thisLaunch }, expectedOrigin);
      };
      if (!inline.panel.proof(new URL(path, expectedOrigin).href, answer)) answer(false);
      return;
    }
    if (data.type === "nc:price") {
      if (!inlineActive() || !priceEl) return;
      if (data.units === null && data.currency === null) {
        priceEl.textContent = "Price available after choices";
        return;
      }
      if (!Number.isSafeInteger(data.units) || data.units < 0 || !/^[A-Z]{3}$/.test(data.currency ?? "")) return;
      try {
        priceEl.textContent = new Intl.NumberFormat("en-US", { style: "currency", currency: data.currency }).format(data.units / 10_000);
      } catch { priceEl.textContent = `${(data.units / 10_000).toFixed(2)} ${data.currency}`; }
      return;
    }
    if (data.type !== "nc:approved") return;
    // Every nc:approved gets an answer (review finding 5), so the popup never waits forever on
    // "Adding it to your cart…". It shows a failure itself, with a retry; this dialog's own error
    // text is only the fallback for a popup that can no longer be reached.
    const reply = (ok, message, viewCart = false) => {
      try {
        iframe.contentWindow.postMessage(buildCartResultMessage(ok, message, viewCart), expectedOrigin);
      } catch {
        if (!ok) showError(message);
      }
    };
    const result = validateApproved(data, productVariantIds(root));
    if (!result.ok) {
      reply(false, "This design no longer matches your selection. Please review it again.");
      return;
    }
    // Inline: the page's quantity field stays usable beside the open panel (a dialog covers it), so it
    // is read again now; a value that is not a quantity is asked for, never guessed.
    const quantity = inlineActive() ? currentQuantity(root, doc) : launchQuantity;
    if (quantity === null) {
      reply(false, "Please enter a quantity from 1 to 9999 on the page, then add it to your cart again.");
      return;
    }
    showError("");
    button.disabled = true;
    button.title = ADDING_TEXT;
    adding.add(result.approved.approvalRef);
    const addLaunch = launchId;
    addApprovedDesignToCart({ fetchImpl: win.fetch.bind(win), cartAddUrl, cartUrl, approved: result.approved, gate, quantity }).then((added) => {
      adding.delete(result.approved.approvalRef);
      button.disabled = false;
      button.removeAttribute("title");
      // Its launch ended meanwhile (the dialog was closed or reopened, the block disposed): never navigate
      // or close anything. The outcome goes beside the button (a reply into the closed iframe would be
      // dropped unseen): a failure, or that the design is in the cart, with a link to it (review 09-25) —
      // unless this same add was already delivered in its own launch (a repeated nc:approved). An outcome
      // that needs a look at the cart (part of it, or maybe all of it, is there) links to it.
      if (addLaunch !== launchId) {
        if (!added.ok) {
          showPageStatus("");
          showPageError(cartOutcomeText(added), cartNeedsLook(added));
        } else if (!delivered.has(added)) {
          showPageStatus("Added to your cart.", true);
        }
        return;
      }
      if (!added.ok) {
        reply(false, cartOutcomeText(added), cartNeedsLook(added));
        return;
      }
      delivered.add(added);
      reply(true);
      if (inlineActive()) inline.panel.added(); // the panel stays, saying so; then the same cart destination
      else closeDialog();
      const dest = chooseCartDestination(doc, root.dataset.cartDrawerSelector, cartPath(win));
      if (dest.action === "click") {
        launcherClicking = true; // the drawer trigger is never a native buy path, whatever it matches
        try {
          dest.el.click();
        } finally {
          launcherClicking = false;
        }
      } else win.location.assign(dest.url);
    });
  });
}

/** Wires every block in `scope` (default: the whole page), including `scope` itself when it is one.
 *  A block already wired is left alone. */
export function initAll(doc, win, scope) {
  const d = doc || document;
  const w = win || window;
  const within = scope || d;
  const roots = [...within.querySelectorAll("[data-nc-personalize]")];
  if (typeof within.matches === "function" && within.matches("[data-nc-personalize]")) roots.unshift(within);
  roots.forEach((root) => wireBlock(root, d, w));
}

/** The entry point: wires the page's blocks, then follows the theme editor's events (they bubble
 * from the section or block; outside the editor they never fire). Returns a stop function. */
export function startLauncher(doc, win) {
  initAll(doc, win);
  const onLoad = (event) => {
    disposeWithin(null); // any block the editor removed without an unload
    if (event.target && typeof event.target.querySelectorAll === "function") initAll(doc, win, event.target);
  };
  const onUnload = (event) => {
    if (event.target) disposeWithin(event.target);
  };
  doc.addEventListener("shopify:section:load", onLoad);
  doc.addEventListener("shopify:block:select", onLoad);
  doc.addEventListener("shopify:section:unload", onUnload);
  return () => {
    doc.removeEventListener("shopify:section:load", onLoad);
    doc.removeEventListener("shopify:block:select", onLoad);
    doc.removeEventListener("shopify:section:unload", onUnload);
  };
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => startLauncher(document, window));
  else startLauncher(document, window);
}
